// src/context/AdminContext.jsx
import { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { useAuth } from './AuthContext'
import { n8nApi } from '../services/n8nApi'

const AdminContext = createContext({})

const STORAGE_KEY = 'fleetvision_impersonation'
const EXPIRY_MINUTES = 60

// Helper to get initial impersonation state from localStorage (synchronous)
const getStoredImpersonation = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const data = JSON.parse(stored)
      if (data.expiresAt > Date.now()) {
        return data
      } else {
        localStorage.removeItem(STORAGE_KEY)
      }
    }
  } catch {
    localStorage.removeItem(STORAGE_KEY)
  }
  return null
}

export const useAdmin = () => {
  const context = useContext(AdminContext)
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider')
  }
  return context
}

export const AdminProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth()
  const [isAdmin, setIsAdmin] = useState(false)
  const [adminInfo, setAdminInfo] = useState(null)
  const [isCheckingAdmin, setIsCheckingAdmin] = useState(true)
  const [prospects, setProspects] = useState([])
  const [isLoadingProspects, setIsLoadingProspects] = useState(false)
  const [error, setError] = useState(null)
  
  // Impersonation state - initialize synchronously from localStorage
  const [impersonating, setImpersonating] = useState(getStoredImpersonation)

  // Demo mode state - allows admins to preview any onboarding step
  const [demoMode, setDemoMode] = useState(null) // null or { stage: '06_API_SETUP' }

  // Clear impersonation on logout (but NOT during initial auth loading)
  useEffect(() => {
    // Only clear when auth has finished loading AND user is null (actual logout)
    if (!authLoading && !user) {
      setImpersonating(null)
      localStorage.removeItem(STORAGE_KEY)
    }
  }, [user, authLoading])

  // Check admin status when user changes
  useEffect(() => {
    const checkAdmin = async () => {
      if (authLoading) {
        return
      }

      if (!user?.uid) {
        setIsAdmin(false)
        setAdminInfo(null)
        setIsCheckingAdmin(false)
        return
      }

      setIsCheckingAdmin(true)
      try {
        const response = await n8nApi.checkAdminStatus(user.uid)
        setIsAdmin(response.is_admin)
        if (response.is_admin) {
          setAdminInfo({
            role: response.role,
            name: response.name
          })
        }
      } catch (err) {
        console.error('Failed to check admin status:', err)
        setIsAdmin(false)
      } finally {
        setIsCheckingAdmin(false)
      }
    }

    checkAdmin()
  }, [user?.uid, authLoading])

  const fetchProspects = useCallback(async () => {
    if (!user?.uid || !isAdmin) return

    setIsLoadingProspects(true)
    setError(null)

    try {
      const response = await n8nApi.getAllProspects(user.uid)
      if (response.success) {
        setProspects(response.prospects)
      } else {
        setError(response.error || 'Failed to fetch prospects')
      }
    } catch (err) {
      console.error('Failed to fetch prospects:', err)
      setError('Failed to load prospects')
    } finally {
      setIsLoadingProspects(false)
    }
  }, [user?.uid, isAdmin])

  // Start impersonating a prospect
  const startImpersonation = useCallback((prospect) => {
    // Prevent impersonating yourself
    if (prospect.firebase_uid === user?.uid) {
      console.warn('Cannot impersonate yourself')
      return false
    }

    const data = {
      uid: prospect.firebase_uid,
      fullName: prospect.full_name,
      companyName: prospect.company_name,
      email: prospect.email,
      stage: prospect.onboarding_stage,
      expiresAt: Date.now() + (EXPIRY_MINUTES * 60 * 1000)
    }
    
    setImpersonating(data)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
    return true
  }, [user?.uid])

  // Stop impersonating
  const stopImpersonation = useCallback(() => {
    setImpersonating(null)
    localStorage.removeItem(STORAGE_KEY)
  }, [])

  // Start demo mode - preview any onboarding step with mock data
  const startDemoMode = useCallback((stage) => {
    // Stop any active impersonation first
    setImpersonating(null)
    localStorage.removeItem(STORAGE_KEY)

    setDemoMode({ stage })
  }, [])

  // Stop demo mode
  const stopDemoMode = useCallback(() => {
    setDemoMode(null)
  }, [])

  const value = {
    isAdmin,
    adminInfo,
    isCheckingAdmin,
    prospects,
    isLoadingProspects,
    error,
    fetchProspects,
    // Impersonation
    impersonating,
    isImpersonating: !!impersonating,
    startImpersonation,
    stopImpersonation,
    // Demo mode
    demoMode,
    isDemoMode: !!demoMode,
    startDemoMode,
    stopDemoMode
  }

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  )
}
